<template>
  <!-- Sidebar wrapper -->
  <div
    class="sb-wrapper my-2 rounded-2xl relative transition-all duration-300 overflow-hidden"
    :class="{
      'w-sidebar mx-2 min-w-sidebar': sessionStore.isOpenSidebar,
      'min-w-0 w-0': !sessionStore.isOpenSidebar,
    }"
  >
    <!-- Sidebar content -->
    <div class="absolute w-sidebar flex flex-col h-full">
      <!-- Tabs -->
      <nav class="relative z-0 flex w-full">
        <!-- Chat Tab Link-->
        <button
          data-cy="sidebar-chat-tab"
          @click="setActiveTab('chatTab')"
          class="
            step-chat-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            flex
            overflow-hidden
            py-4
            px-2
            text-center
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'chatTab' }"
        >
          <div class="flex flex- justify-center items-center">
            <ChatAlt2Icon class="h-6 w-6 mr-1" />
            <span
              class="text-xxs font-light text-gray-500"
              :class="{ 'text-gray-900 font-bold': activeTab === 'chatTab' }"
            >
              CHAT
            </span>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'chatTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>

        <!-- Attendees Tab Link-->
        <button
          data-cy="sidebar-attendees-tab"
          @click="setActiveTab('participantsTab')"
          class="
            step-attendees-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            items-center
            flex
            overflow-hidden
            py-4
            px-3
            text-center
            shadow-inner-lg
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'participantsTab' }"
        >
          <div class="flex flex- justify-center items-center">
            <UsersIcon class="h-6 w-6 mr-1" />
            <div class="flex items-center text-xs">
              <span
                class="text-xxs font-light text-gray-500"
                :class="{ 'text-gray-900 font-bold': activeTab === 'participantsTab' }"
              >
                ATTENDEES
              </span>
              <div
                class="leading-tight rounded-xl px-1 bg-gray-100 text-gray-500"
                :class="{ 'text-gray-900 font-bold': activeTab === 'participantsTab' }"
              >
                {{ usersPresenceStore.totalPresentUsers }}
              </div>
            </div>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'participantsTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>

        <!-- Session Tab Link -->
        <button
          v-if="sessionStore.isClinician && sessionStore.snapshotsAllowed"
          data-cy="sidebar-session-tab"
          @click="setActiveTab('sessionTab')"
          class="
            step-chat-tab
            text-gray-500
            group
            relative
            flex-1
            justify-center
            flex
            overflow-hidden
            py-4
            px-2
            text-center
          "
          :class="{ 'text-vice-teal-900 ': activeTab === 'sessionTab' }"
        >
          <div class="flex flex- justify-center items-center">
            <SessionInfoIcon class="h-5 w-5 mr-1" />
            <span
              class="text-xxs font-light text-gray-500"
              :class="{ 'text-gray-900 font-bold': activeTab === 'sessionTab' }"
            >
              SNAPSHOTS
            </span>
          </div>
          <span
            aria-hidden="true"
            class="absolute inset-x-0 bottom-0 h-0.5"
            :class="activeTab === 'sessionTab' ? 'bg-vice-teal-400' : 'bg-gray-300'"
          ></span>
        </button>
      </nav>

      <!-- Chat tab -->
      <ChatLayout
        v-show="activeTab === 'chatTab'"
        :clinicianName="clinicianName"
        :participants-list="participantsList"
        :userIsObserver="sessionStore.userIsObserver"
      />

      <!-- Participants tab -->
      <ParticipantsList
        v-show="activeTab === 'participantsTab'"
        :participants-list="participantsList"
        :latecomers-list="sessionStore.isClinician ? latecomersList : []"
        :muteSingleParticipant="muteSingleParticipant"
        :kickParticipant="kickParticipant"
        @lateEntryResponse="lateEntryResponse"
        @lowerHand="$emit('lowerHand', $event)"
      />

      <!-- Session tab -->
      <SessionLayout v-show="activeTab === 'sessionTab'" :clinicianName="clinicianName" />
      <!-- End of sidebar content -->
    </div>

    <LateEntryToast
      @late-entry-response="lateEntryResponse"
      @toggle-toast="toggleToast('lateEntryToast')"
      :latecomers-list="latecomersList"
      v-if="toasts.lateEntryToast.value && sessionStore.isClinician"
    ></LateEntryToast>

    <StethoscopeConnectionToast
      @stethoscope-connection-allowed="connectionAccepted"
      @toggle-toast="toggleToast('stethoscopeToast')"
      v-if="toasts.stethoscopeToast.value && sessionStore.isClinician"
    ></StethoscopeConnectionToast>

    <!-- End of sidebar wrapper -->
  </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import { computed, onMounted, ref, watch } from 'vue';
import { ChatAlt2Icon, UsersIcon } from '@heroicons/vue/solid';
import ChatLayout from '@/Components/Chat/ChatLayout.vue';
import PhaseService from '@/Services/PhaseService';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import useUsersPresenceStore from '@/Stores/useUsersPresenceStore';
import LateEntryToast from '@/Components/Toasts/LateEntryToast.vue';
import ParticipantsList from '@/Components/Chat/ParticipantsList.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import StethoscopeConnectionToast from '@/Components/Toasts/StethoscopeConnectionToast.vue';
import {
  EXTERNAL_CONNECTED_AUDIO_DEVICES,
  STETHOSCOPE_LABELS,
  USER_ROLE,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import SessionInfoIcon from '@/Components/Icons/SessionInfoIcon.vue';
import SessionLayout from '@/Components/Chat/SessionLayout.vue';

export default {
  components: {
    SessionLayout,
    SessionInfoIcon,
    UsersIcon,
    ChatLayout,
    ChatAlt2Icon,
    LateEntryToast,
    ParticipantsList,
    StethoscopeConnectionToast,
  },
  emits: ['lowerHand'],
  props: {
    muteSingleParticipant: {
      type: Function,
    },
    kickParticipant: {
      type: Function,
    },
  },
  setup() {
    const latecomersList = ref([]);
    const pageProps = usePage().props.value;
    const { session } = pageProps;
    const sessionStore = useSessionStore();
    const streamingDeviceStore = useIOTDevicesStore();
    const usersPresenceStore = useUsersPresenceStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const toasts = { lateEntryToast: ref(false), stethoscopeToast: ref(false) };

    const activeTab = computed(() => sessionStore.sidebarTab);

    const isStethoscopeConnected = computed(
      () =>
        !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.AUDIO.Microphone.find((device) =>
          STETHOSCOPE_LABELS.some((singleLabel) => device.label.includes(singleLabel)),
        ),
    );

    const isCurrentlyStreaming = computed(
      () =>
        !!multiCameraDevicesStore.allExternalConnectedAudioDevices.find(
          (deviceName) => deviceName === EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE,
        ),
    );

    const toggleToast = (toast, toState = null) => {
      toasts[toast].value = typeof toState === 'boolean' ? toState : !toasts[toast].value;
    };

    const connectionAccepted = (accepted) => {
      if (accepted) {
        handleStethoscopeConnection();
      }
      toggleToast('stethoscopeToast', false);
    };

    const setActiveTab = (tabName) => {
      sessionStore.sidebarTab = tabName;
    };

    const handleStethoscopeConnection = async () => {
      try {
        if (isCurrentlyStreaming.value) {
          await multiCameraDevicesStore.removeExternalAudioDevice(
            EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE,
          );
        } else {
          await multiCameraDevicesStore.addExternalAudioDevice(
            sessionStore.sessionId,
            EXTERNAL_CONNECTED_AUDIO_DEVICES.STETHOSCOPE,
            STETHOSCOPE_LABELS,
          );
        }
        multiCameraDevicesStore.markSessionSetupAsChanged();
      } catch (error) {
        console.error({ error });
      }
    };

    watch(isStethoscopeConnected, (newValue, oldValue) => {
      if (
        newValue &&
        sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION &&
        sessionStore.hasLivePhase
      ) {
        toggleToast('stethoscopeToast', true);

        setTimeout(() => {
          toggleToast('stethoscopeToast', false);
        }, 5000);
      } else if (!newValue && oldValue) {
        toggleToast('stethoscopeToast', false);

        if (isCurrentlyStreaming.value) {
          handleStethoscopeConnection();
        }
      }
    });

    const participants = session.users
      .filter((invitedUser) => invitedUser.user_type !== USER_ROLE.OBSERVER)
      .map((participant) => ({
        id: participant.id,
        name: participant.name,
        avatarUrl: participant.profile_photo_url,
      }));

    const clinicianInfo = {
      id: session.details.clinician?.id,
      name: session.details.clinician?.name,
      avatarUrl: session.details.clinician?.profile_photo_url,
    };

    const participantsList = [clinicianInfo, ...participants];

    const lateEntryResponse = (user, accepted) => {
      const data = {
        uuid: session.unique_id,
        userUuid: user.id,
        accepted,
      };

      PhaseService.lateEntryRequest(data).then(() => {
        window.Echo.private(`late.entry.${session.unique_id}`).whisper('response', data);
      });

      const index = latecomersList.value.findIndex((obj) => obj.id === user.id);
      if (index !== -1) {
        latecomersList.value.splice(index, 1);
      }
    };

    window.Echo.join(`presence.channel.${session.unique_id}`)
      .here((users) => {
        usersPresenceStore.initUsers(users);
      })
      .joining((joiningUser) => {
        usersPresenceStore.addUser(joiningUser);
      })
      .leaving((leavingUser) => {
        usersPresenceStore.removeUser(leavingUser);
      })
      .error((error) => {
        console.error(error);
      });

    onMounted(async () => {
      if (session.late_entry) {
        window.Echo.private(`late.entry.${session.unique_id}`).listenForWhisper(
          'entry-request',
          (request) => {
            const index = latecomersList.value.findIndex((obj) => obj.id === request.id);
            if (index !== -1) {
              latecomersList.value.splice(index, 1);
            }
            latecomersList.value.push(request);
            toggleToast('lateEntryToast', true);

            setTimeout(() => {
              toggleToast('lateEntryToast', false);
            }, 5000);
          },
        );
      }
    });

    return {
      toasts,
      activeTab,
      toggleToast,
      sessionStore,
      setActiveTab,
      latecomersList,
      participantsList,
      lateEntryResponse,
      connectionAccepted,
      usersPresenceStore,
      streamingDeviceStore,
      isStethoscopeConnected,
      clinicianName: clinicianInfo.name,
    };
  },
};
</script>

<style>
.sb-wrapper {
  background-color: #f5f5f5;
}
</style>
